import React, { useEffect, useMemo, useState } from 'react'
import withSlideUpModal from '../../utils/hoc/withSlideUpModalComponent'
import { ApplicationData, Requirements as CampaignRequirements, ScreeningQuestionType } from '../../types/sponsorships/campaigns'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux-stores/store'
import IgConnectBenefits from '../igConnect/IgConnectBenefits'
import { Link, useLocation } from 'react-router-dom'
import { INIT_IG_AUTH_ROUTE } from '../../constants/constants'
import { setIntendedPath } from '../../redux-stores/slices/layoutSlice'
import { AppDispatch } from '../../redux-stores/slices/authSlice'
import Select from 'react-select'
import { SocialAccount } from '../../types/influencer/influencerTypes'
import ScreeningQuestionMCQ from './ScreeningQuestion/ScreeningQuestionMCQ'
import ScreeningQuestionBinary from './ScreeningQuestion/ScreeningQuestionBinary'
import ScreeningQuestionText from './ScreeningQuestion/ScreeningQuestionText'
import { toast } from 'react-toastify'
import { POSTAPIError } from '../../types'
import { ERROR } from '../../constants/errors'
const CampaignApplyConfirmationModal: React.FC<{ application: ApplicationData, applicationError?: POSTAPIError | null, isActive: boolean, apply: (languageId?: string, answers?: Record<string, string | number>) => void, status: string, campaignRequirements?: CampaignRequirements, socialAccount: SocialAccount }> = ({ application, applicationError, isActive, apply, status, campaignRequirements, socialAccount }) => {
    const dispatch: AppDispatch = useDispatch();
    const location = useLocation();
    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);
    const [selectedLanguage, setSelectedLanguage] = useState<{ label: string, value: string } | null>(null);
    const languages = useSelector((state: RootState) => state.auth.master?.language_list);

    const [answers, setAnswers] = useState<Record<string, string | number> | undefined>(undefined);
    useEffect(() => {
        if (Array.isArray(application.questions) && application.questions.length > 0) {
            const newAnswers = {} as Record<string, string>;
            for (let i = 0; i < application.questions.length; i++) {
                newAnswers[application.questions[i].id.toString()] = '';
            }
            setAnswers(newAnswers);
        } else {
            setAnswers(undefined);
        }
    }, [application.questions])

    const matchedCriteria = useMemo(() => {
        return Object.values(application.match?.requirement_matches ?? {}).filter((req) => req.match)
    }, [application]);

    const notMatchedCriteria = useMemo(() => {
        return Object.values(application.match?.requirement_matches ?? {}).filter((req) => !req.match)
    }, [application]);

    useEffect(() => {
        setSelectedLanguage(null);
    }, [application])

    const eligibleLanguages = useMemo(() => {
        let campaignLanguages: { label: string, value: string }[] = [];
        if (campaignRequirements && Array.isArray(campaignRequirements?.languages)) {
            for (let languageId of campaignRequirements.languages) {
                if (languages && languages[languageId]) {
                    campaignLanguages.push({ label: languages[languageId], value: languageId });
                }
            }
        }
        return campaignLanguages;
    }, [campaignRequirements, languages])


    const allQuestionsAnswered = useMemo(() => {
        if (!application.questions || application.questions.length === 0) return true;
        return application.questions.every((question) => answers?.[question.id.toString()] !== '');
    }, [application.questions, answers]);

    const applyCampaign = () => {
        if (!allQuestionsAnswered) {
            toast.error('Please answer all questions');
            return;
        }
        if (eligibleLanguages.length > 0 && !selectedLanguage) {
            toast.error('Please select a language');
            return;
        }
        apply(selectedLanguage?.value, answers);
    }

    return (
        <div className='campaign-apply-confirmation scrollable-modal-body p-0'>
            <div className="d-flex flex-column gap-075">
            </div>
            {
                application.questions && application.questions.length > 0 &&
                <div className="d-flex flex-column gap-4  p-3">
                    {
                        application.questions?.map((question, index) => {
                            const highlightError = (applicationError && applicationError.error_code === ERROR.INVALID_ANSWER && applicationError.question_id === question.id) ? true : false;
                            if (question.type === ScreeningQuestionType.CHOICE) {
                                return <ScreeningQuestionMCQ highlightError={highlightError} key={question.id} index={index} question={question} value={answers?.[question.id.toString()] ?? ''} onChange={(value) => setAnswers({ ...answers, [question.id.toString()]: value })} />
                            }
                            if (question.type === ScreeningQuestionType.BINARY) {
                                return <ScreeningQuestionBinary highlightError={highlightError} key={question.id} index={index} question={question} value={answers?.[question.id.toString()] ?? ''} onChange={(value) => setAnswers({ ...answers, [question.id.toString()]: value })} />
                            }
                            if (question.type === ScreeningQuestionType.TEXT) {
                                return <ScreeningQuestionText key={question.id} index={index} question={question} value={answers?.[question.id.toString()] ?? ''} onChange={(value) => setAnswers({ ...answers, [question.id.toString()]: value })} />
                            }
                        })
                    }
                </div>
            }
            <div className="campaign-apply-ig-connect-benefit-actions scrollable-modal-footer">
                <div className='px-3'>
                    <div>
                        <div className="form-group gap-2 d-flex flex-column mb-3">
                            <div className="d-flex gap-2">
                                <img src={(socialAccount.profile_picture_url ?? '').split("=")[0]} alt="" className='rounded-sm' width={50} height={50} />
                                <div className="d-flex flex-column">
                                    <label htmlFor="">Applying using</label>
                                    <span className='text-dark fs-16 fw-500'>{socialAccount.handle}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {
                        eligibleLanguages.length > 0 &&
                        <div className="profile-input my-2 mt-2 mb-3">
                            <label htmlFor="">Please select language which you will use</label>
                            <Select maxMenuHeight={100} isClearable classNamePrefix={'influns-app'} className="profile-input-form-control-wrapper" placeholder='Select Language*' menuPlacement='top' options={eligibleLanguages}
                                onChange={(selected: any) => setSelectedLanguage(selected)} />
                        </div>
                    }
                    <div className={`campaign-apply-disclaimer mb-2`}>
                        <span className="fw-bold">Disclaimer: </span>
                        <span>Application selection is decided by brand.</span>
                    </div>
                    <button onClick={applyCampaign} disabled={status === 'PENDING' || (eligibleLanguages.length > 0 && !selectedLanguage) || !allQuestionsAnswered} className="btn btn-primary w-100">{status === 'PENDING' ? 'Applying...' : 'Apply'}</button>
                </div>
            </div>

        </div>
    )
}

export default withSlideUpModal(CampaignApplyConfirmationModal, "Complete Application", true)